import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '@/utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';


export function AdminTransactions({ headers }) {
  const [transactions, setTransactions] = useState([]);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [type, setType] = useState('all');

  const fetchTransactions = async () => {
    try {
      const params = {};
      if (search) params.search = search;
      if (status !== 'all') params.status = status;
      if (type !== 'all') params.type = type;
      const res = await axios.get(`${API}/admin/transactions`, { headers, params });
      setTransactions(res.data || []);
    } catch (e) {
      console.error('Failed to load transactions');
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, [search, status, type]);

  const formatDate = (iso) => {
    try { return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }); }
    catch { return '-'; }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>All Transactions</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-3">
          <Input
            placeholder="Search by name, description, ID..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="max-w-xs"
            data-testid="transactions-search"
          />
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-44" data-testid="transactions-status-filter">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="COMPLETED">Completed</SelectItem>
              <SelectItem value="PENDING">Pending</SelectItem>
              <SelectItem value="CANCELLED">Cancelled</SelectItem>
              <SelectItem value="paid">Paid (P2P)</SelectItem>
              <SelectItem value="failed">Failed (P2P)</SelectItem>
            </SelectContent>
          </Select>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger className="w-44" data-testid="transactions-type-filter">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="direct_payment">Direct Payments</SelectItem>
              <SelectItem value="transaction">Completed Transactions</SelectItem>
              <SelectItem value="payment">P2P Payments</SelectItem>
              <SelectItem value="request">P2P Requests</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="overflow-x-auto border rounded-lg">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left">
              <tr>
                <th className="p-3">Type</th>
                <th className="p-3">From</th>
                <th className="p-3">To</th>
                <th className="p-3">Amount</th>
                <th className="p-3">Sub-Account</th>
                <th className="p-3">Status</th>
                <th className="p-3">Date</th>
              </tr>
            </thead>
            <tbody>
              {transactions.map((t, i) => (
                <tr key={t.id || i} className="border-t">
                  <td className="p-3">
                    <Badge variant="outline" className="text-xs">{t.type}</Badge>
                    {t.is_guest && (
                      <span className="block text-[10px] text-violet-600 font-medium mt-0.5">Guest</span>
                    )}
                  </td>
                  <td className="p-3">{t.from_name || '-'}</td>
                  <td className="p-3">{t.to_name || '-'}</td>
                  <td className="p-3 font-medium">{t.amount ?? '-'} {t.amount ? 'SEK' : ''}</td>
                  <td className="p-3 text-xs">{t.sub_account_name || '-'}</td>
                  <td className="p-3">
                    <Badge variant={
                      t.status === 'COMPLETED' || t.status === 'paid' ? 'default' :
                      t.status === 'PENDING' ? 'secondary' :
                      'destructive'
                    } className="text-xs">{t.status || '-'}</Badge>
                  </td>
                  <td className="p-3 text-xs">{formatDate(t.created_at)}</td>
                </tr>
              ))}
              {transactions.length === 0 && (
                <tr>
                  <td className="p-4 text-center text-muted-foreground" colSpan={7}>No transactions found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="text-xs text-muted-foreground">Showing {transactions.length} records</div>
      </CardContent>
    </Card>
  );
}
